import { useEffect, useState } from "react";
import { Printer, ArrowLeft, BookOpen, MapPin } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { COMUNI_ALL } from "../lib/constants";
import QRWithLogo from "../components/QRWithLogo";
import ComuneCombobox from "../components/ComuneCombobox";
import ShareMenu from "../components/ShareMenu";
import { Button } from "../components/ui/button";

const VALID = COMUNI_ALL;

export default function Locandina() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = VALID.includes(searchParams.get("comune")) ? searchParams.get("comune") : "Fiano Romano";
  const [comune, setComune] = useState(initial);
  const [books, setBooks] = useState([]);

  const changeComune = (c) => { setComune(c); setSearchParams({ comune: c }); };

  useEffect(() => {
    api.get("/books", { params: { comune } }).then(({ data }) => setBooks(data.filter((b) => !b.is_demo).slice(0, 12)));
  }, [comune]);

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const shareUrl = `${origin}/locandina?comune=${encodeURIComponent(comune)}`;
  const today = new Date().toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" });

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 print-a4">
      <div className="flex items-center justify-between gap-3 mb-6 no-print flex-wrap">
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-2"><ArrowLeft className="w-4 h-4 mr-2" />Indietro</Button>
          <h1 className="text-2xl sm:text-3xl font-serif font-bold tracking-tight">Locandina comune</h1>
          <p className="text-sm text-muted-foreground mt-1">Un volantino A4 dedicato a un singolo comune, con intestazione e data.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
          <div className="w-full sm:w-52">
            <ComuneCombobox value={comune} onChange={changeComune} testId="locandina-comune" placeholder="Cerca comune…" triggerClassName="h-9" />
          </div>
          <ShareMenu url={shareUrl} text={`Libri disponibili a ${comune} su Libri in Circolo: ${shareUrl}`} align="end" testid="locandina-share" />
          <Button data-testid="locandina-print" onClick={() => window.print()} className="flex-1 sm:flex-none bg-primary hover:bg-primary/90"><Printer className="w-4 h-4 mr-2" />Stampa</Button>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-white text-stone-900 overflow-hidden" data-testid="locandina-sheet">
        <div className="bg-[#8C3A27] text-white px-8 py-8 text-center">
          <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest opacity-90 mb-3"><BookOpen className="w-4 h-4" />Libri in Circolo</div>
          <h2 className="font-serif text-3xl sm:text-4xl font-bold leading-tight">Scambio libri gratuito</h2>
          <p className="mt-2 flex items-center justify-center gap-1.5 text-lg"><MapPin className="w-5 h-5" />{comune}</p>
          <p className="mt-1 text-sm opacity-80">Aggiornato al {today}</p>
        </div>

        <div className="px-8 py-6">
          <p className="text-center text-stone-600 text-sm mb-6">Inquadra un codice con il telefono per vedere il libro e proporre uno scambio con un altro cittadino. Nessun costo, nessuna spedizione.</p>
          {books.length === 0 ? (
            <div className="text-center py-12 text-stone-400"><BookOpen className="w-8 h-8 mx-auto mb-2" />Nessun libro disponibile a {comune}.</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-5">
              {books.map((b) => (
                <div key={b.id} className="flex flex-col items-center text-center">
                  <QRWithLogo value={`${origin}/libro/${b.id}`} size={100} />
                  <p className="font-serif font-semibold text-sm mt-2 leading-tight line-clamp-2">{b.title}</p>
                  <p className="text-xs text-stone-500 line-clamp-1">{b.author}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-stone-100 text-center py-3 text-xs text-stone-500">Libri in Circolo · la comunità che fa girare i libri</div>
      </div>
    </div>
  );
}
